import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Mic, Bot, AlertTriangle } from 'lucide-react';
import { ReplaceAudioModal } from '../../components/admin/ReplaceAudioModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SRC = {
  tts:       { label: 'Rowdy Voice TTS', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Bot },
  recording: { label: 'Rowdy Recording', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: Mic },
  missing:   { label: 'Missing Audio',   cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: AlertTriangle },
};

const MeditationRow = ({ m, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [replace, setReplace] = useState(false);
  const s = SRC[m.audioSource] || SRC.missing;
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-3" data-testid={`program-meditation-${m.slug}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-stone-800">{m.title}</p>
          <p className="text-[10.5px] text-stone-500 font-mono break-all">id {m.id} · {m.slug}</p>
          <p className="text-[11px] text-stone-600 mt-1">
            {m.programs.length ? m.programs.map((p) => `${p.program} · Day ${p.day}`).join('  ·  ') : 'Not used by any program yet'}
          </p>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${s.cls}`} data-testid={`program-meditation-source-${m.slug}`}>
          <s.Icon className="w-3 h-3" /> {s.label}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-3 flex-wrap">
        {m.audioUrl && (
          <audio controls preload="none" src={`${API_URL}${m.audioUrl}`} className="h-8 max-w-[320px]" data-testid={`program-meditation-audio-${m.slug}`} />
        )}
        {m.durationSeconds && <span className="text-[11px] text-stone-500">~{Math.round(m.durationSeconds / 60)} min target</span>}
        <button onClick={() => setOpen((v) => !v)} className="text-[11px] font-semibold text-[#C79A4A]" data-testid={`program-meditation-script-toggle-${m.slug}`}>
          {open ? 'Hide script' : 'View script'}
        </button>
        <button onClick={() => setReplace(true)} className="ml-auto px-2.5 h-7 rounded-full border border-stone-300 text-[11px] font-semibold inline-flex items-center gap-1" data-testid={`program-meditation-replace-${m.slug}`}>
          <Mic className="w-3 h-3" /> Replace Audio{m.audioVersion > 1 ? ` · v${m.audioVersion}` : ''}
        </button>
        <ReplaceAudioModal contentId={m.id} open={replace} onClose={() => setReplace(false)} onChanged={onChanged} />
      </div>
      {open && (
        <pre className="mt-2 whitespace-pre-wrap text-[12px] leading-relaxed text-stone-700 bg-[#FFFCF6] border border-stone-100 rounded-lg p-3" data-testid={`program-meditation-script-${m.slug}`}>
          {(m.ttsScript || '').replace(/\[pause\]/g, '\n\n[pause]\n\n')}
        </pre>
      )}
    </div>
  );
};

export const ProgramMeditationsTab = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const load = () => {
    const token = localStorage.getItem('rbr_token');
    fetch(`${API_URL}/api/admin/program-meditations`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null)).then(setData).catch(() => setData({ items: [], counts: {} }));
  };
  useEffect(() => { load(); }, []);
  const items = useMemo(() => (data?.items || []).filter((m) => filter === 'all' || m.audioSource === filter), [data, filter]);
  if (!data) return <p className="text-sm text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading canonical meditations…</p>;
  const c = data.counts || {};
  return (
    <div className="space-y-3" data-testid="program-meditations-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-base font-semibold text-stone-800 mr-2">Program meditations</h3>
        {[['all', `All ${c.total ?? 0}`], ['tts', `TTS ${c.tts ?? 0}`], ['recording', `Recording ${c.recording ?? 0}`], ['missing', `Missing ${c.missing ?? 0}`]].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`program-meditations-filter-${k}`}
            className={`px-2.5 py-1 rounded-full text-[11px] border ${filter === k ? 'bg-[#173F35] text-white border-[#173F35]' : 'bg-white text-stone-600 border-stone-200'}`}>
            {label}
          </button>
        ))}
      </div>
      <p className="text-[12px] text-stone-500">One canonical record per meditation — programs reference the same ID. Replacing audio later keeps the ID, progress and analytics intact.</p>
      {items.map((m) => <MeditationRow key={m.id} m={m} onChanged={load} />)}
    </div>
  );
};

export default ProgramMeditationsTab;
